import { createFileRoute, useRouter } from "@tanstack/react-router";
import type { FriendRequestView } from "@/lib/types";
import {
  loadFriends,
  sendFriendRequestFn,
  acceptRequestFn,
  rejectRequestFn,
  cancelRequestFn,
  removeFriendFn,
} from "@/lib/server-fns/friends";
import {
  Box,
  Container,
  Heading,
  Button,
  Table,
  Input,
  HStack,
  Card,
  Text,
  VStack,
  Dialog,
  Portal,
  CloseButton,
  Field,
  Badge,
  Flex,
  Separator,
} from "@chakra-ui/react";
import { showErrorToast } from "@/lib/toast-utils";
import { UserNameCell } from "@/components/user-name-cell";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { LuUserPlus, LuCheck, LuX } from "react-icons/lu";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/friends")({
  loader: () => loadFriends(),
  component: Friends,
});

function getRequestDisplayName(req: FriendRequestView, direction: "incoming" | "outgoing"): string {
  const user = direction === "incoming" ? req.fromUser : req.toUser;
  return user?.name || user?.email || (direction === "incoming" ? req.fromUserId : req.toUserId);
}

function getRequestEmail(req: FriendRequestView, direction: "incoming" | "outgoing"): string {
  const user = direction === "incoming" ? req.fromUser : req.toUser;
  return user?.email || "";
}

function Friends() {
  const { friends, incoming, outgoing } = Route.useLoaderData();
  const router = useRouter();
  const [addOpened, setAddOpened] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{
    userId: string;
    displayName: string;
  } | null>(null);

  const hasPendingRequests = incoming.length > 0 || outgoing.length > 0;

  const handleAction = async (
    action: (args: { data: Record<string, string> }) => Promise<void>,
    data: Record<string, string>,
    errorTitle: string,
  ) => {
    try {
      await action({ data });
      router.invalidate();
    } catch (error) {
      showErrorToast(errorTitle, error);
    }
  };

  const handleSendRequest = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    setAddOpened(false);
    await handleAction(sendFriendRequestFn, { email }, "Failed to send request");
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemoveTarget(null);
    await handleAction(removeFriendFn, { userId: removeTarget.userId }, "Failed to remove friend");
  };

  return (
    <Container maxW={{ base: "full", md: "4xl" }} px={{ base: "3", md: "4" }}>
      <Flex
        justify="space-between"
        align={{ base: "stretch", sm: "center" }}
        direction={{ base: "column", sm: "row" }}
        gap="3"
        mb="4"
      >
        <HStack gap="3">
          <Heading as="h2" size="lg">
            Friends
          </Heading>
          {incoming.length > 0 && (
            <Badge colorPalette="orange" variant="solid">
              {incoming.length} pending
            </Badge>
          )}
        </HStack>
        <Button onClick={() => setAddOpened(true)} width={{ base: "full", sm: "auto" }}>
          <LuUserPlus size={16} />
          Send Friend Request
        </Button>
      </Flex>

      {/* Pending incoming requests */}
      {incoming.length > 0 && (
        <Card.Root variant="outline" mb="4">
          <Card.Header pb="2">
            <Heading as="h3" size="md">
              Incoming Requests
            </Heading>
          </Card.Header>
          <Card.Body pt="0" p={{ base: "3", md: "4" }}>
            {/* Desktop table */}
            <Box hideBelow="md">
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>From</Table.ColumnHeader>
                    <Table.ColumnHeader>Email</Table.ColumnHeader>
                    <Table.ColumnHeader>Actions</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {incoming.map((req) => {
                    const name = getRequestDisplayName(req, "incoming");
                    const email = getRequestEmail(req, "incoming");

                    return (
                      <Table.Row key={req.id}>
                        <Table.Cell>
                          <UserNameCell
                            name={req.fromUser?.name || ""}
                            identifier={email || req.fromUserId}
                            displayName={name}
                            fontWeight="medium"
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Text color="fg.muted">{email}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <HStack gap="1">
                            <Button
                              size="xs"
                              colorPalette="green"
                              onClick={() =>
                                handleAction(
                                  acceptRequestFn,
                                  { requestId: req.id },
                                  "Failed to accept request",
                                )
                              }
                            >
                              <LuCheck size={14} />
                              Accept
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              onClick={() =>
                                handleAction(
                                  rejectRequestFn,
                                  { requestId: req.id },
                                  "Failed to reject request",
                                )
                              }
                            >
                              <LuX size={14} />
                              Reject
                            </Button>
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table.Root>
            </Box>

            {/* Mobile stacked view */}
            <VStack gap="3" align="stretch" hideFrom="md">
              {incoming.map((req, index) => {
                const name = getRequestDisplayName(req, "incoming");
                const email = getRequestEmail(req, "incoming");

                return (
                  <Box key={req.id}>
                    {index > 0 && <Separator mb="3" />}
                    <Flex justify="space-between" align="center" mb="2">
                      <Box minW="0" flex="1">
                        <UserNameCell
                          name={req.fromUser?.name || ""}
                          identifier={email || req.fromUserId}
                          displayName={name}
                          fontWeight="medium"
                          truncate
                        />
                      </Box>
                    </Flex>
                    {email && (
                      <Text color="fg.muted" fontSize="sm" mb="2" truncate>
                        {email}
                      </Text>
                    )}
                    <HStack gap="2">
                      <Button
                        size="sm"
                        colorPalette="green"
                        flex="1"
                        onClick={() =>
                          handleAction(
                            acceptRequestFn,
                            { requestId: req.id },
                            "Failed to accept request",
                          )
                        }
                      >
                        <LuCheck size={14} />
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="red"
                        flex="1"
                        onClick={() =>
                          handleAction(
                            rejectRequestFn,
                            { requestId: req.id },
                            "Failed to reject request",
                          )
                        }
                      >
                        <LuX size={14} />
                        Reject
                      </Button>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          </Card.Body>
        </Card.Root>
      )}

      {/* Pending outgoing requests */}
      {outgoing.length > 0 && (
        <Card.Root variant="outline" mb="4">
          <Card.Header pb="2">
            <Heading as="h3" size="md">
              Sent Requests
            </Heading>
          </Card.Header>
          <Card.Body pt="0" p={{ base: "3", md: "4" }}>
            {/* Desktop table */}
            <Box hideBelow="md">
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>To</Table.ColumnHeader>
                    <Table.ColumnHeader>Email</Table.ColumnHeader>
                    <Table.ColumnHeader>Actions</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {outgoing.map((req) => {
                    const name = getRequestDisplayName(req, "outgoing");
                    const email = getRequestEmail(req, "outgoing");

                    return (
                      <Table.Row key={req.id}>
                        <Table.Cell>
                          <UserNameCell
                            name={req.toUser?.name || ""}
                            identifier={email || req.toUserId}
                            displayName={name}
                            fontWeight="medium"
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Text color="fg.muted">{email}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="red"
                            onClick={() =>
                              handleAction(
                                cancelRequestFn,
                                { requestId: req.id },
                                "Failed to cancel request",
                              )
                            }
                          >
                            <LuX size={14} />
                            Cancel
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table.Root>
            </Box>

            {/* Mobile stacked view */}
            <VStack gap="3" align="stretch" hideFrom="md">
              {outgoing.map((req, index) => {
                const name = getRequestDisplayName(req, "outgoing");
                const email = getRequestEmail(req, "outgoing");

                return (
                  <Box key={req.id}>
                    {index > 0 && <Separator mb="3" />}
                    <Flex justify="space-between" align="center">
                      <Box minW="0" flex="1">
                        <UserNameCell
                          name={req.toUser?.name || ""}
                          identifier={email || req.toUserId}
                          displayName={name}
                          fontWeight="medium"
                          truncate
                        />
                        {email && (
                          <Text color="fg.muted" fontSize="sm" truncate mt="1">
                            {email}
                          </Text>
                        )}
                      </Box>
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="red"
                        flexShrink={0}
                        ml="3"
                        onClick={() =>
                          handleAction(
                            cancelRequestFn,
                            { requestId: req.id },
                            "Failed to cancel request",
                          )
                        }
                      >
                        <LuX size={14} />
                        Cancel
                      </Button>
                    </Flex>
                  </Box>
                );
              })}
            </VStack>
          </Card.Body>
        </Card.Root>
      )}

      {/* Friends list */}
      <Card.Root variant="outline">
        <Card.Body p={{ base: "3", md: "4" }}>
          {friends.length === 0 && !hasPendingRequests ? (
            <VStack align="center" gap="4" py="16">
              <Text fontSize="lg" color="fg.muted">
                No friends yet
              </Text>
              <Text fontSize="sm" color="fg.muted">
                Add friends to split expenses with them
              </Text>
              <Button onClick={() => setAddOpened(true)}>
                <LuUserPlus size={16} />
                Send Friend Request
              </Button>
            </VStack>
          ) : friends.length === 0 ? (
            <VStack align="center" gap="2" py="8">
              <Text fontSize="sm" color="fg.muted">
                No accepted friends yet
              </Text>
            </VStack>
          ) : (
            <>
              {/* Desktop table */}
              <Box hideBelow="md">
                <Table.Root>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Friend</Table.ColumnHeader>
                      <Table.ColumnHeader>Email</Table.ColumnHeader>
                      <Table.ColumnHeader>Actions</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {friends.map((friend) => {
                      const name = friend.user?.name || "";
                      const email = friend.user?.email || "";

                      return (
                        <Table.Row key={friend.id}>
                          <Table.Cell>
                            <UserNameCell
                              name={name}
                              identifier={email || friend.userId}
                              displayName={name || email}
                              fontWeight="medium"
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <Text color="fg.muted">{email}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              onClick={() =>
                                setRemoveTarget({
                                  userId: friend.userId,
                                  displayName: name || email,
                                })
                              }
                            >
                              Remove
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table.Root>
              </Box>

              {/* Mobile stacked view */}
              <VStack gap="3" align="stretch" hideFrom="md">
                {friends.map((friend, index) => {
                  const name = friend.user?.name || "";
                  const email = friend.user?.email || "";

                  return (
                    <Box key={friend.id}>
                      {index > 0 && <Separator mb="3" />}
                      <Flex justify="space-between" align="center">
                        <Box minW="0" flex="1">
                          <UserNameCell
                            name={name}
                            identifier={email || friend.userId}
                            displayName={name || email}
                            fontWeight="medium"
                            truncate
                          />
                          {email && (
                            <Text color="fg.muted" fontSize="sm" truncate mt="1">
                              {email}
                            </Text>
                          )}
                        </Box>
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          flexShrink={0}
                          ml="3"
                          onClick={() =>
                            setRemoveTarget({
                              userId: friend.userId,
                              displayName: name || email,
                            })
                          }
                        >
                          Remove
                        </Button>
                      </Flex>
                    </Box>
                  );
                })}
              </VStack>
            </>
          )}
        </Card.Body>
      </Card.Root>

      {/* Add friend dialog */}
      <Dialog.Root
        open={addOpened}
        onOpenChange={(e) => setAddOpened(e.open)}
        size={{ base: "full", sm: "md" }}
        placement={{ base: "bottom", sm: "center" }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Send Friend Request</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <form onSubmit={handleSendRequest} id="add-friend-form">
                  <Field.Root required>
                    <Field.Label>Email</Field.Label>
                    <Field.HelperText>Send a friend request by email</Field.HelperText>
                    <Input name="email" type="email" placeholder="friend@example.com" />
                  </Field.Root>
                </form>
              </Dialog.Body>
              <Dialog.Footer>
                <Button type="submit" form="add-friend-form" width="full">
                  Send Request
                </Button>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Remove friend confirmation dialog */}
      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        title="Remove friend"
        description={`Are you sure you want to remove ${removeTarget?.displayName} from your friends list?`}
        confirmLabel="Remove"
        onConfirm={handleRemove}
      />
    </Container>
  );
}
